import api from './api';

export interface Review {
  id: number;
  lawyer_id: number;
  client_id: number;
  case_id: number;
  rating: number; // 1-5 stars
  title: string;
  content: string;
  professionalism: number;
  communication: number;
  expertise: number;
  value_for_money: number;
  overall_experience: number;
  would_recommend: boolean;
  is_verified: boolean;
  is_anonymous: boolean;
  created_at: string;
  updated_at: string;
  // Populated fields
  lawyer_details?: ReviewLawyer;
  client_details?: ReviewClient;
  case_details?: ReviewCase;
}

export interface ReviewLawyer {
  id: number;
  first_name: string;
  last_name: string;
  avatar_url?: string;
  specialization: string;
}

export interface ReviewClient {
  id: number;
  first_name: string;
  last_name: string;
  avatar_url?: string;
}

export interface ReviewCase {
  id: number;
  subject: string;
  type: string;
}

export interface CreateReviewInput {
  lawyer_id: number;
  case_id: number;
  rating: number;
  title: string;
  content: string;
  professionalism: number;
  communication: number;
  expertise: number;
  value_for_money: number;
  overall_experience: number;
  would_recommend: boolean;
  is_anonymous: boolean;
}

export interface ReviewStats {
  average_rating: number;
  total_reviews: number;
  rating_distribution: { [key: number]: number }; // 1-5 stars counts
  category_averages: {
    professionalism: number;
    communication: number;
    expertise: number;
    value_for_money: number;
    overall_experience: number;
  };
  would_recommend_percentage: number;
}

const reviewService = {
  getReviews: async (lawyerId?: number, clientId?: number, caseId?: number) => {
    const params = new URLSearchParams();
    if (lawyerId) params.append('lawyer_id', lawyerId.toString());
    if (clientId) params.append('client_id', clientId.toString());
    if (caseId) params.append('case_id', caseId.toString());
    
    const response = await api.get(`/reviews?${params.toString()}`);
    return response.data;
  },

  getReviewById: async (id: number) => {
    const response = await api.get(`/reviews/${id}`);
    return response.data;
  },

  createReview: async (data: CreateReviewInput) => {
    const response = await api.post('/reviews', data);
    return response.data;
  },

  updateReview: async (id: number, data: Partial<CreateReviewInput>) => {
    const response = await api.put(`/reviews/${id}`, data);
    return response.data;
  },

  deleteReview: async (id: number) => {
    const response = await api.delete(`/reviews/${id}`);
    return response.data;
  },

  getLawyerStats: async (lawyerId: number) => {
    const response = await api.get(`/reviews/lawyer/${lawyerId}/stats`);
    return response.data;
  },

  getOrganizationStats: async (orgId: number) => {
    const response = await api.get(`/reviews/organization/${orgId}/stats`);
    return response.data;
  },

  verifyReview: async (id: number) => {
    const response = await api.put(`/reviews/${id}/verify`);
    return response.data;
  },

  reportReview: async (id: number, reason: string) => {
    const response = await api.post(`/reviews/${id}/report`, { reason });
    return response.data;
  },

  searchReviews: async (query: string, filters?: {
    lawyer_id?: number;
    rating?: number;
    would_recommend?: boolean;
    is_verified?: boolean;
  }) => {
    const params = new URLSearchParams({ query });
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value !== undefined) params.append(key, value.toString());
    });
    
    const response = await api.get(`/reviews/search?${params.toString()}`);
    return response.data;
  },
};

export default reviewService;
